'use client'
import type { MarketId } from '@morpho-org/blue-sdk'
import type { ColumnDefWithSkeleton } from '@morpho-org/ui-v6/types'

import { InterfaceChainUtils } from '@morpho-org/dapp-core'
import { formatLongString, isDefined } from '@morpho-org/morpho-ts'
import { TokenInfo, WarningIcon } from '@morpho-org/ui-v6/client'
import { NetworkAvatar } from '@morpho-org/ui-v6/components/client/NetworkAvatar'
import { TextAddress } from '@morpho-org/ui-v6/components/client/TextAddress'
import CellTokenPrice from '@morpho-org/ui-v6/components/server/CellTokenPrice'
import { Skeleton } from '@morpho-org/ui-v6/components/server/Skeleton'
import { formatters, getDigitsFromPrice } from '@morpho-org/ui-v6/utils'
import { Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui'
import {
  columnOrderingFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  rowSortingFeature,
  tableFeatures
} from '@tanstack/react-table'

import CellNetApy from '@/components/common/Cells/CellNetApy'
import { CollateralYieldTags } from '@/components/common/CollateralYieldTags'
import { AVG_APY_DURATION_LABEL } from '@/constants/avgApy'

import type { TableMarketRow } from './TableMarket.types'

import MarketTrustedByCuratorStack from '../components/MarketTrustedByCuratorStack'
import { COLUMN_IDS, COLUMN_SIZES, MARKETS_COLUMN_LABELS } from './config'

const percentFormatter = formatters.percent.digits(2).unit('%')

export const tableMarketFeatures = tableFeatures({
  columnOrderingFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  rowSortingFeature
})
export type TableMarketFeatures = typeof tableMarketFeatures

export const marketColumnsDefs: ColumnDefWithSkeleton<TableMarketFeatures, TableMarketRow>[] = [
  {
    id: COLUMN_IDS.Network,
    size: COLUMN_SIZES[COLUMN_IDS.Network],
    accessorFn: row => row.network.chainId,
    enableSorting: false,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-secondary-foreground">
            {MARKETS_COLUMN_LABELS[COLUMN_IDS.Network]}
          </span>
        </TooltipTrigger>
        <TooltipContent>The network of the market.</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => {
      const { chainId } = row.original.network
      const chainName = InterfaceChainUtils.getMetadata(chainId).chain.name
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center">
              <NetworkAvatar chainId={chainId} srcAvatarProps={{ size: 'medium' }} />
            </div>
          </TooltipTrigger>
          <TooltipContent>{chainName}</TooltipContent>
        </Tooltip>
      )
    },
    skeletonCell: <Skeleton className="size-5 rounded-full" />
  },
  {
    id: COLUMN_IDS.LoanAssetSymbol,
    size: COLUMN_SIZES[COLUMN_IDS.LoanAssetSymbol],
    accessorFn: row => row.market.loanAsset.symbol,
    enableSorting: true,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-secondary-foreground">
            {MARKETS_COLUMN_LABELS[COLUMN_IDS.LoanAssetSymbol]}
          </span>
        </TooltipTrigger>
        <TooltipContent>The asset that is lent and borrowed.</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => {
      const { loanAsset } = row.original.market
      return <TokenInfo token={loanAsset} chainId={loanAsset.chain.id} />
    },
    skeletonCell: <Skeleton className="h-[22px] w-[100px]" />
  },
  {
    id: COLUMN_IDS.CollateralAssetSymbol,
    size: COLUMN_SIZES[COLUMN_IDS.CollateralAssetSymbol],
    accessorFn: row => row.market.collateralAsset?.symbol,
    enableSorting: true,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-secondary-foreground">
            {MARKETS_COLUMN_LABELS[COLUMN_IDS.CollateralAssetSymbol]}
          </span>
        </TooltipTrigger>
        <TooltipContent>The asset used as collateral.</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => {
      const {
        collateralAsset,
        loanAsset,
        warnings,
        hasUserPosition,
        collateralNativeYield,
        collateralRewards
      } = row.original.market
      if (!collateralAsset || !loanAsset) return null
      return (
        <div className="gap-xs flex min-w-0 items-center">
          <TokenInfo
            token={collateralAsset}
            renderWarnings={<WarningIcon warnings={warnings} />}
            chainId={loanAsset.chain.id}
            renderUserPosition={
              hasUserPosition && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="bg-success-foreground size-3 shrink-0 rounded-full" />
                  </TooltipTrigger>
                  <TooltipContent>You have an active position in this market.</TooltipContent>
                </Tooltip>
              )
            }
          />
          <CollateralYieldTags
            nativeYield={collateralNativeYield}
            collateralRewards={collateralRewards}
          />
        </div>
      )
    },
    skeletonCell: <Skeleton className="h-[22px] w-[120px]" />
  },
  {
    id: COLUMN_IDS.Lltv,
    size: COLUMN_SIZES[COLUMN_IDS.Lltv],
    accessorFn: row => row.lltv,
    enableSorting: true,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-secondary-foreground">
            {MARKETS_COLUMN_LABELS[COLUMN_IDS.Lltv]}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          The loan-to-value ratio at which your position is eligible for liquidation.
        </TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => (
      <span className="text-foreground text-xs">{(row.original.lltv * 100).toFixed(2)}%</span>
    ),
    skeletonCell: <Skeleton className="h-md w-[40px]" />
  },
  {
    id: COLUMN_IDS.TrustedBy,
    size: COLUMN_SIZES[COLUMN_IDS.TrustedBy],
    enableSorting: false,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-secondary-foreground">
            {MARKETS_COLUMN_LABELS[COLUMN_IDS.TrustedBy]}
          </span>
        </TooltipTrigger>
        <TooltipContent>Vault Curators that allocate into this market</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => {
      const curators = row.original.trustedBy.curators
      if (curators.length === 0) return null
      return (
        <MarketTrustedByCuratorStack
          curators={curators}
          // oxlint-disable-next-line typescript/consistent-type-assertions
          marketId={row.original.market.marketId as MarketId}
          avatarSize="medium"
          maxTokensCount={2}
        />
      )
    },
    skeletonCell: <Skeleton className="h-md w-[60px]" />
  },
  {
    id: COLUMN_IDS.BorrowAssetsUsd,
    size: COLUMN_SIZES[COLUMN_IDS.BorrowAssetsUsd],
    accessorFn: row => row.borrowAssetsUsd.valueUsd,
    enableSorting: true,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-secondary-foreground">
            {MARKETS_COLUMN_LABELS[COLUMN_IDS.BorrowAssetsUsd]}
          </span>
        </TooltipTrigger>
        <TooltipContent>The total amount currently borrowed from the market.</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => (
      <CellTokenPrice
        token={row.original.market.loanAsset}
        amount={row.original.borrowAssetsUsd.amount}
        value={row.original.borrowAssetsUsd.valueUsd}
      />
    ),
    skeletonCell: (
      <div className="gap-2xs flex flex-col items-start">
        <Skeleton className="h-(--text-xs--line-height) w-[100px]" />
        <Skeleton className="h-md w-[60px] rounded-full" />
      </div>
    )
  },
  {
    id: COLUMN_IDS.TotalLiquidityUsd,
    size: COLUMN_SIZES[COLUMN_IDS.TotalLiquidityUsd],
    accessorFn: row => row.totalLiquidityUsd.valueUsd,
    enableSorting: true,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-secondary-foreground">
            {MARKETS_COLUMN_LABELS[COLUMN_IDS.TotalLiquidityUsd]}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          The sum of market liquidity and reallocatable liquidity available for borrowing.
        </TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => (
      <CellTokenPrice
        token={row.original.market.loanAsset}
        amount={row.original.totalLiquidityUsd.amount}
        value={row.original.totalLiquidityUsd.valueUsd}
      />
    ),
    skeletonCell: (
      <div className="gap-2xs flex flex-col items-start">
        <Skeleton className="h-(--text-xs--line-height) w-[100px]" />
        <Skeleton className="h-md w-[60px] rounded-full" />
      </div>
    )
  },
  {
    id: COLUMN_IDS.AvgNetBorrowApy,
    size: COLUMN_SIZES[COLUMN_IDS.AvgNetBorrowApy],
    accessorFn: row => row.avgNetBorrowApy.avgNetBorrowApy,
    enableSorting: true,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-secondary-foreground ml-[14px]">
            {MARKETS_COLUMN_LABELS[COLUMN_IDS.AvgNetBorrowApy]}
          </span>
        </TooltipTrigger>
        <TooltipContent>{`Average borrow rate for the past ${AVG_APY_DURATION_LABEL}.`}</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => (
      <CellNetApy
        variant="market"
        performanceFee={0n}
        netApy={row.original.avgNetBorrowApy.avgNetBorrowApy}
        apy={row.original.avgNetBorrowApy.avgBorrowApy}
        rewards={row.original.avgNetBorrowApy.rewards}
        points={row.original.avgNetBorrowApy.points}
        asset={row.original.avgNetBorrowApy.asset}
        merklContext={{
          kind: 'market',
          chainId: row.original.network.chainId,
          marketId: row.original.market.marketId,
          loanTokenAddress: row.original.market.loanAsset.address
        }}
      />
    ),
    skeletonCell: (
      <div className="ml-[14px]">
        <Skeleton className="h-md w-8" />
      </div>
    )
  },
  {
    id: COLUMN_IDS.NetSupplyApy,
    size: COLUMN_SIZES[COLUMN_IDS.NetSupplyApy],
    accessorFn: row => row.netSupplyApy.netSupplyApy,
    enableSorting: true,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-secondary-foreground ml-[14px]">
            {MARKETS_COLUMN_LABELS[COLUMN_IDS.NetSupplyApy]}
          </span>
        </TooltipTrigger>
        <TooltipContent>{`Average lend rate for the past ${AVG_APY_DURATION_LABEL}.`}</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => (
      <CellNetApy
        variant="supply"
        performanceFee={0n}
        netApy={row.original.netSupplyApy.netSupplyApy}
        apy={row.original.netSupplyApy.supplyApy}
        rewards={row.original.netSupplyApy.rewards}
        points={row.original.netSupplyApy.points}
        asset={row.original.netSupplyApy.asset}
        merklContext={{
          kind: 'market',
          chainId: row.original.network.chainId,
          marketId: row.original.market.marketId,
          loanTokenAddress: row.original.market.loanAsset.address
        }}
      />
    ),
    skeletonCell: (
      <div className="ml-[14px]">
        <Skeleton className="h-md w-8" />
      </div>
    )
  },
  {
    id: COLUMN_IDS.Utilization,
    size: COLUMN_SIZES[COLUMN_IDS.Utilization],
    accessorFn: row => row.utilization,
    enableSorting: true,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-secondary-foreground">
            {MARKETS_COLUMN_LABELS[COLUMN_IDS.Utilization]}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          Amount in % of supplied assets that has been borrowed on the market.
        </TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => (
      <span className="text-foreground text-xs">
        {percentFormatter.of(row.original.utilization)}
      </span>
    ),
    skeletonCell: <Skeleton className="h-(--text-xs--line-height) w-[46px]" />
  },
  {
    id: COLUMN_IDS.LiquidityAssets,
    size: COLUMN_SIZES[COLUMN_IDS.LiquidityAssets],
    accessorFn: row => row.liquidityAssets.valueUsd,
    enableSorting: true,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-secondary-foreground">
            {MARKETS_COLUMN_LABELS[COLUMN_IDS.LiquidityAssets]}
          </span>
        </TooltipTrigger>
        <TooltipContent>The total amount available to borrow.</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => (
      <CellTokenPrice
        token={row.original.market.loanAsset}
        amount={row.original.liquidityAssets.amount}
        value={row.original.liquidityAssets.valueUsd}
      />
    ),
    skeletonCell: (
      <div className="gap-2xs flex flex-col items-start">
        <Skeleton className="h-(--text-xs--line-height) w-[100px]" />
        <Skeleton className="h-md w-[60px] rounded-full" />
      </div>
    )
  },
  {
    id: COLUMN_IDS.CollateralAssetsUsd,
    size: COLUMN_SIZES[COLUMN_IDS.CollateralAssetsUsd],
    accessorFn: row => row.collateralAssetsUsd.valueUsd,
    enableSorting: true,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-secondary-foreground">
            {MARKETS_COLUMN_LABELS[COLUMN_IDS.CollateralAssetsUsd]}
          </span>
        </TooltipTrigger>
        <TooltipContent>The total amount of collateral on the market.</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => {
      if (!row.original.market.collateralAsset) return '-'
      return (
        <CellTokenPrice
          token={row.original.market.collateralAsset}
          amount={row.original.collateralAssetsUsd.amount}
          value={row.original.collateralAssetsUsd.valueUsd}
        />
      )
    },
    skeletonCell: (
      <div className="gap-2xs flex flex-col items-start">
        <Skeleton className="h-(--text-xs--line-height) w-[100px]" />
        <Skeleton className="h-md w-[60px] rounded-full" />
      </div>
    )
  },
  {
    id: COLUMN_IDS.NetBorrowApy,
    size: COLUMN_SIZES[COLUMN_IDS.NetBorrowApy],
    accessorFn: row => row.netBorrowApy.netBorrowApy,
    enableSorting: true,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-secondary-foreground ml-[14px]">
            {MARKETS_COLUMN_LABELS[COLUMN_IDS.NetBorrowApy]}
          </span>
        </TooltipTrigger>
        <TooltipContent>The instant borrow APY of the market.</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => {
      if (!isDefined(row.original.netBorrowApy.netBorrowApy)) return null
      return (
        <CellNetApy
          variant="market"
          performanceFee={0n}
          netApy={row.original.netBorrowApy.netBorrowApy}
          apy={row.original.netBorrowApy.borrowApy}
          rewards={row.original.netBorrowApy.rewards}
          points={row.original.netBorrowApy.points}
          asset={row.original.netBorrowApy.asset}
          merklContext={{
            kind: 'market',
            chainId: row.original.network.chainId,
            marketId: row.original.market.marketId,
            loanTokenAddress: row.original.market.loanAsset.address
          }}
        />
      )
    },
    skeletonCell: (
      <div className="ml-[14px]">
        <Skeleton className="h-md w-8" />
      </div>
    )
  },
  {
    id: COLUMN_IDS.WeeklyBorrowApy,
    size: COLUMN_SIZES[COLUMN_IDS.WeeklyBorrowApy],
    accessorFn: row => row.weeklyBorrowApy.weeklyNetBorrowApy,
    enableSorting: true,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-secondary-foreground ml-[14px]">
            {MARKETS_COLUMN_LABELS[COLUMN_IDS.WeeklyBorrowApy]}
          </span>
        </TooltipTrigger>
        <TooltipContent>The average borrow APY for the past 7 days.</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => {
      if (!isDefined(row.original.weeklyBorrowApy.weeklyNetBorrowApy)) return null
      return (
        <CellNetApy
          variant="market"
          performanceFee={0n}
          netApy={row.original.weeklyBorrowApy.weeklyNetBorrowApy}
          apy={row.original.weeklyBorrowApy.weeklyBorrowApy}
          rewards={row.original.weeklyBorrowApy.rewards}
          points={row.original.weeklyBorrowApy.points}
          asset={row.original.weeklyBorrowApy.asset}
          merklContext={{
            kind: 'market',
            chainId: row.original.network.chainId,
            marketId: row.original.market.marketId,
            loanTokenAddress: row.original.market.loanAsset.address
          }}
        />
      )
    },
    skeletonCell: (
      <div className="ml-[14px]">
        <Skeleton className="h-md w-8" />
      </div>
    )
  },
  {
    id: COLUMN_IDS.SupplyAssetsUsd,
    size: COLUMN_SIZES[COLUMN_IDS.SupplyAssetsUsd],
    accessorFn: row => row.supplyAssetsUsd.valueUsd,
    enableSorting: true,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-secondary-foreground">
            {MARKETS_COLUMN_LABELS[COLUMN_IDS.SupplyAssetsUsd]}
          </span>
        </TooltipTrigger>
        <TooltipContent>The amount supplied to the market.</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => (
      <CellTokenPrice
        token={row.original.market.loanAsset}
        amount={row.original.supplyAssetsUsd.amount}
        value={row.original.supplyAssetsUsd.valueUsd}
      />
    ),
    skeletonCell: (
      <div className="gap-2xs flex flex-col items-start">
        <Skeleton className="h-(--text-xs--line-height) w-[100px]" />
        <Skeleton className="h-md w-[60px] rounded-full" />
      </div>
    )
  },
  {
    id: COLUMN_IDS.SizeUsd,
    size: COLUMN_SIZES[COLUMN_IDS.SizeUsd],
    accessorFn: row => row.sizeUsd.valueUsd,
    enableSorting: true,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-secondary-foreground">
            {MARKETS_COLUMN_LABELS[COLUMN_IDS.SizeUsd]}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          The sum of all borrowed funds, market liquidity, and reallocatable liquidity.
        </TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => (
      <CellTokenPrice
        token={row.original.market.loanAsset}
        amount={row.original.sizeUsd.amount}
        value={row.original.sizeUsd.valueUsd}
      />
    ),
    skeletonCell: (
      <div className="gap-2xs flex flex-col items-start">
        <Skeleton className="h-(--text-xs--line-height) w-[100px]" />
        <Skeleton className="h-md w-[60px] rounded-full" />
      </div>
    )
  },
  {
    id: COLUMN_IDS.OraclePrice,
    size: COLUMN_SIZES[COLUMN_IDS.OraclePrice],
    accessorFn: row => row.oraclePrice.price,
    enableSorting: true,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-secondary-foreground">
            {MARKETS_COLUMN_LABELS[COLUMN_IDS.OraclePrice]}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          Collateral price quoted in the Loan asset returned by the market&apos;s oracle.
        </TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => {
      const { price, collateralDecimals, loanDecimals, loanSymbol } = row.original.oraclePrice
      if (!row.original.market.collateralAsset || !isDefined(price)) return '-'
      const decimals = Math.max(0, Math.min(100, 36 + loanDecimals - (collateralDecimals ?? 0)))
      return (
        <span className="text-foreground text-xs">
          {formatters.commas
            .digits(getDigitsFromPrice(price, decimals))
            .unit(formatLongString(loanSymbol, 16))
            .of(price, decimals)}
        </span>
      )
    },
    skeletonCell: <Skeleton className="h-(--text-xs--line-height) w-[100px]" />
  },
  {
    id: COLUMN_IDS.IrmAddress,
    size: COLUMN_SIZES[COLUMN_IDS.IrmAddress],
    enableSorting: false,
    header: () => (
      <span className="text-secondary-foreground">
        {MARKETS_COLUMN_LABELS[COLUMN_IDS.IrmAddress]}
      </span>
    ),
    cell: ({ row }) => (
      <TextAddress
        address={row.original.irmAddress}
        toastMessage="Interest rate model address copied to clipboard."
        tooltipMessage="Copy interest rate model address."
      />
    ),
    skeletonCell: <Skeleton className="h-(--text-xs--line-height) w-[115px]" />
  },
  {
    id: COLUMN_IDS.BorrowToTarget,
    size: COLUMN_SIZES[COLUMN_IDS.BorrowToTarget],
    accessorFn: row => row.borrowToTarget,
    enableSorting: true,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-secondary-foreground">
            {MARKETS_COLUMN_LABELS[COLUMN_IDS.BorrowToTarget]}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          Liquidity left to borrow before the market reaches target utilization, including both
          market and Public Allocator liquidity.
        </TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => (
      <span className="text-foreground text-xs">
        {formatters.short.digits(2).default('-').unit('$').of(row.original.borrowToTarget, 18)}
      </span>
    ),
    skeletonCell: <Skeleton className="h-(--text-xs--line-height) w-[46px]" />
  },
  {
    id: COLUMN_IDS.ApyAtTarget,
    size: COLUMN_SIZES[COLUMN_IDS.ApyAtTarget],
    accessorFn: row => row.apyAtTarget,
    enableSorting: true,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-secondary-foreground">
            {MARKETS_COLUMN_LABELS[COLUMN_IDS.ApyAtTarget]}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          Rate when the market reaches utilization target. This rate can evolve slowly over time
          according to market forces.
        </TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => (
      <span className="text-foreground text-xs">
        {percentFormatter.of(row.original.apyAtTarget)}
      </span>
    ),
    skeletonCell: <Skeleton className="h-(--text-xs--line-height) w-[46px]" />
  },
  {
    id: COLUMN_IDS.UniqueKey,
    size: COLUMN_SIZES[COLUMN_IDS.UniqueKey],
    enableSorting: false,
    header: () => (
      <span className="text-secondary-foreground">
        {MARKETS_COLUMN_LABELS[COLUMN_IDS.UniqueKey]}
      </span>
    ),
    cell: ({ row }) => (
      <TextAddress
        address={row.original.market.marketId}
        toastMessage="Market ID copied to clipboard."
        tooltipMessage="Copy market ID."
      />
    ),
    skeletonCell: <Skeleton className="h-(--text-xs--line-height) w-[115px]" />
  },
  {
    id: COLUMN_IDS.BadDebt,
    size: COLUMN_SIZES[COLUMN_IDS.BadDebt],
    accessorFn: row => row.badDebt.usd,
    enableSorting: true,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-secondary-foreground">
            {MARKETS_COLUMN_LABELS[COLUMN_IDS.BadDebt]}
          </span>
        </TooltipTrigger>
        <TooltipContent>The amount of unrealized bad debt currently on the market.</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => (
      <CellTokenPrice
        token={row.original.market.loanAsset}
        amount={row.original.badDebt.underlying}
        value={row.original.badDebt.usd}
      />
    ),
    skeletonCell: (
      <div className="gap-2xs flex flex-col items-start">
        <Skeleton className="h-(--text-xs--line-height) w-[100px]" />
        <Skeleton className="h-md w-[60px] rounded-full" />
      </div>
    )
  },
  {
    id: COLUMN_IDS.RealizedBadDebt,
    size: COLUMN_SIZES[COLUMN_IDS.RealizedBadDebt],
    accessorFn: row => row.realizedBadDebt.usd,
    enableSorting: true,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-secondary-foreground">
            {MARKETS_COLUMN_LABELS[COLUMN_IDS.RealizedBadDebt]}
          </span>
        </TooltipTrigger>
        <TooltipContent>The sum of all past realized bad debt on the market.</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => (
      <CellTokenPrice
        token={row.original.market.loanAsset}
        amount={row.original.realizedBadDebt.underlying}
        value={row.original.realizedBadDebt.usd}
      />
    ),
    skeletonCell: (
      <div className="gap-2xs flex flex-col items-start">
        <Skeleton className="h-(--text-xs--line-height) w-[100px]" />
        <Skeleton className="h-md w-[60px] rounded-full" />
      </div>
    )
  }
]
